import * as React from 'react';
import { DeliveredReport, ReportRecipientReport } from './models';
import { lastCommaFirstLocal } from '../project/survey/raters/module';
import Msg from '../localization/Msg';
import { Str } from '../localization/translationKeys';
import DownloadIcon from '../icons/DownloadIcon';
import { COLORS } from '../globalStyle/colors';
import { TextDirection } from '../localization/textDirection';
import SendIcon from '../icons/SendIcon';
import { btnStyle } from '../forms/styles/button';
import Spacer from '../layout/Spacer';
import { css } from '@emotion/core';
import LocalDiv from '../localization/LocalDiv';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserAlt, faUsers } from '@fortawesome/free-solid-svg-icons';
import { FLEX_ROW, FLEX_COLUMN } from '../layout/flexBox';
import { pathCombine } from '../core/api/module';
import { BaseUrl } from '../core/api/endpoints';
import { useMount } from '../core/hooks/useMount';
import { useBrowser } from '../language/useBrowser';
import { useAppState } from '../core/redux/hooks';
import DeliverReportsModal from './DeliverReportsModal';
import { useToggle } from '../core/hooks/useToggle';
import DataTable from '../layout/DataTable';


const styles = {
  deliverText:      css`
    color: ${COLORS.BLUE};
    font-size: 12px;
  `,
  link:             css`
    text-decoration: none;
    color: ${COLORS.BLUE};
    cursor:pointer;

    &:hover { 
      text-decoration: underline;
    }
  `,
  linkDisabled:       css`
    cursor: default;
    opacity: .6;

    &:hover { 
      text-decoration: none;
      cursor: default;
    }
  `,
  button:             css`
    color: ${COLORS.BLUE};

    &:hover { 
      text-decoration: underline;
      cursor: pointer;
    }
  `,
  tipTrigger:         css`
    cursor: default;
    position: relative;
    display: inline;
    color: ${COLORS.BLUE};  

    &:hover { 
      cursor: pointer;
    }

    &:hover div { 
        display: block;
    }
  `,
  toolTip:            css`
    display: none;
    background-color: ${COLORS.WHITE};
    color: ${COLORS.DARK_GREY};  
    position: absolute;
    top: -37px;
    right: calc(100% + 5px);
    border: 2px solid ${COLORS.LIGHT_GREY};
    min-width: 320px;
    z-index: 1;
    
    & th {
      background-color: ${COLORS.WHITE};
      padding: 4px 8px;
      border: none;
      white-space: nowrap;
    }    
    & td {
      background-color: ${COLORS.WHITE};
      padding: 4px 8px;
      border: none;
      white-space: nowrap;
    }
  }
  `,
}

type Props = {
  className?: string;
  report: ReportRecipientReport;
  textDirection: TextDirection;
  hasDeliverPermission: boolean;
  hasDeliverThirdPartyPermission: boolean;
  deliverClicked: (report: ReportRecipientReport) => void;
  deliverClickedTo: (
    reports: ReportRecipientReport[], 
    toParticipant: boolean, 
    firstName: string, 
    lastName: string, 
    email: string,
  ) => void;
  redeliverClicked: (report: ReportRecipientReport) => void;
  downloadClicked: () => void;
  reportClicked: (report: ReportRecipientReport, selected: boolean) => void;
  singleReport: boolean;
  differentLanguages: boolean;
};

/**
 * ReportTableRow: a component that presents information about a report, as a table row
 *
 * PureComponent: true
 * Redux Connected: false
 */
const ReportTableRow: React.FC<Props> = props => {
  const delivered  = React.useMemo(
    () => new Date(props.report.deliveryDate ?? new Date()),
    [props.report.deliveryDate],
  );

  const fileURL = (report: ReportRecipientReport) => {
    const baseUrl = pathCombine(BaseUrl, '/report/reportFile');
    return report?.fileExists ? baseUrl + '/' + encodeURIComponent(report!.reportFile) : undefined
  };

  const downloadURL = (report: ReportRecipientReport) => {
    const baseUrl = pathCombine(BaseUrl, '/report/reportDownload');
    return report?.fileExists ? baseUrl + '/' + encodeURIComponent(report!.reportFile) : undefined
  };

  const reportType = (report: ReportRecipientReport, differentLanguages: boolean) => {
    const typeStr = report.isComposite ? Str.Composite : report.isSummary ? Str.Summary: Str.Individual;
    return differentLanguages
    ? <><Msg str={typeStr} /> ({report.languageName})</>
    : <Msg str={typeStr} />
  };

  const code = useAppState(state => state.locale.lang.code);
  const [delivering, toggleDeliverModal] = useToggle();

  useMount(() => {
    props.singleReport && props.reportClicked(props.report, true)
  });

  const { onIe } = useBrowser();

  const showTip = props.report.deliveryNames.length > 0 && props.report.deliveryNames !== '---';

  const mapDeliveriesToRows = React.useMemo(
    () => {
      const reports = new Array<DeliveredReport>();

      const deliveryNames = props.report.deliveryNamesForToolTip.split('|');
      const deliveryDates = props.report.deliveryDatesForToolTip.split('|');

      if (deliveryDates.length < deliveryNames.length) {
        //this shouldn't happen, but avoids crash if it does
        for (let i = deliveryDates.length; i < deliveryNames.length; i++) {
          deliveryDates.push('?');
        }
      }

      for (let i = 0; i < deliveryNames.length; i++) {
        const key = props.report.participantReportId + '|' + i.toString();

        const report = {
          deliveredTo: deliveryNames[i],
          deliveryDate: deliveryDates[i],
          rowKey: key,
        };

        reports.push(report);
      }

      return reports.map(
        report =>
          <React.Fragment key={report.rowKey}>
            <tr>
              <td>{report.deliveredTo}</td>
              <td>{report.deliveryDate}</td>
            </tr>
          </React.Fragment>
,
      )
    },
    [
      props.report.deliveryNamesForToolTip,
      props.report.deliveryDatesForToolTip,
      props.report.participantReportId,
    ],
  );

  return (
    <>
      <tr key={props.report.participantReportId} css={props.className}>
        <td>
          <LocalDiv css={FLEX_ROW}>
            <FontAwesomeIcon
              icon={props.report.isComposite ? faUsers : faUserAlt}
            />
            <Spacer sizePx={5} css={FLEX_COLUMN}/>
            <LocalDiv css={FLEX_COLUMN}>
              {lastCommaFirstLocal(props.textDirection, props.report)}
            </LocalDiv>
          </LocalDiv>
        </td>
        <td>
          {
            props.report.isComposite
            ? <Msg str={Str.NA} />
            : props.report.email
          }
        </td>
        <td>
          {delivered.toLocaleDateString(code)}&nbsp;{delivered.toLocaleTimeString(code)}
        </td>
        <td>
          {
            props.hasDeliverPermission && !props.report.portalDelivered
            ? props.hasDeliverThirdPartyPermission
              ? <LocalDiv onClick={toggleDeliverModal} css={[FLEX_ROW, styles.link]}>
                  <Msg str={Str.Deliver} css={[FLEX_COLUMN, styles.deliverText]}/>
                  <Spacer sizePx={5} css={FLEX_COLUMN} />
                  <SendIcon sizePx={14} fill={COLORS.BLUE} css={FLEX_COLUMN} />
                </LocalDiv>
              : <button
                  type='button'
                  onClick={() => props.deliverClicked(props.report)}
                  css={[btnStyle.invisible, styles.button]}
                >
                  <LocalDiv css={FLEX_ROW}>
                    <Msg str={Str.Deliver} css={[FLEX_COLUMN, styles.deliverText]}/>
                    <Spacer sizePx={5} css={FLEX_COLUMN} />
                    <SendIcon sizePx={14} fill={COLORS.BLUE} css={FLEX_COLUMN} />
                  </LocalDiv>
                </button>
            : showTip 
              ? <LocalDiv css={styles.tipTrigger}>
                  {props.report.deliveryNames}
                  <LocalDiv css={styles.toolTip}>
                    <DataTable
                      headers={[
                        <Msg str={Str.DeliveredTo} />,
                        <Msg str={Str.DateDelivered} />,
                      ]}
                    >
                      {mapDeliveriesToRows}
                    </DataTable>
                  </LocalDiv>
                </LocalDiv>
              : props.report.deliveryNames
          }
        </td>
        <td>
          { props.report.webReport
            ? <a
                href={props.report.reportUrl}
                target={'_blank'}
                rel='noopener noreferrer'
                css={styles.link}
              >
                {reportType(props.report, props.differentLanguages)}
              </a>          
            : onIe
              ? reportType(props.report, props.differentLanguages)
              : <a
                  href={fileURL(props.report)}
                  target={'_blank'}
                  rel='noopener noreferrer'
                  css={[styles.link, !props.report.fileExists && styles.linkDisabled]}
                >
                  {reportType(props.report, props.differentLanguages)}
                </a>
          }
        </td>
        <td>
          <a 
            href={downloadURL(props.report)}
            css={[btnStyle.invisible, styles.button, !props.report.fileExists && styles.linkDisabled]}
            onClick={() => props.downloadClicked}
          >
            <DownloadIcon sizePx={20} fill={COLORS.BLUE} />
          </a>        
        </td>
        <td>
          <input 
            type='checkbox'
            checked={props.report.isChecked || props.singleReport || false}
            onChange={(event) => !props.singleReport && props.reportClicked(props.report, event.target.checked)}
            disabled={props.singleReport}
          />
        </td>
      </tr>
      {
        delivering && (
          <DeliverReportsModal
            reports={[props.report]}
            deliverReport={props.deliverClickedTo}
            dismiss={toggleDeliverModal}
          />
        )
      }
    </>
  )
};

export default ReportTableRow;
